import { z } from 'zod';
import { refersTo } from '../common/refers-to';

const REGION_EPSILON = 1e-6;

export const RedactionRegionSchema = z
  .object({
    pageIndex: z.number().int().nonnegative(),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().gt(0).max(1),
    height: z.number().gt(0).max(1),
  })
  .refine((r) => r.x + r.width <= 1 + REGION_EPSILON && r.y + r.height <= 1 + REGION_EPSILON, {
    message: 'region exceeds page',
  });
export type RedactionRegion = z.infer<typeof RedactionRegionSchema>;

export const REDACTABLE_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export type RedactableContentType = (typeof REDACTABLE_CONTENT_TYPES)[number];

export const REDACTION_MAX_REGIONS = 500;

export function isRedactableContentType(contentType: string): contentType is RedactableContentType {
  return (REDACTABLE_CONTENT_TYPES as ReadonlyArray<string>).includes(contentType);
}

const REDACTABLE_EXTENSION = /\.(pdf|jpe?g|png)$/i;

/** "Grundbuch 4711.pdf" → { base: "Grundbuch 4711", extension: ".pdf" }; no redactable extension → extension "". */
export function splitDocumentNameExtension(name: string): { base: string; extension: string } {
  const extension = REDACTABLE_EXTENSION.exec(name)?.[0];

  if (extension === undefined) return { base: name, extension: '' };

  return { base: name.slice(0, -extension.length), extension };
}

export const StartDocumentRedactionBodySchema = z.object({
  copyName: z.string().trim().min(1).max(240),
  regions: z.array(RedactionRegionSchema).min(1).max(REDACTION_MAX_REGIONS),
});
export type StartDocumentRedactionBody = z.infer<typeof StartDocumentRedactionBodySchema>;

export const DOCUMENT_REDACTION_STATUSES = ['RUNNING', 'SUCCEEDED', 'FAILED'] as const;
export type DocumentRedactionStatus = (typeof DOCUMENT_REDACTION_STATUSES)[number];
export const DOCUMENT_REDACTION_FAILURE_REASONS = ['ENGINE_FAILED', 'CHECK_FAILED'] as const;
export type DocumentRedactionFailureReason = (typeof DOCUMENT_REDACTION_FAILURE_REASONS)[number];

export const DocumentRedactionSchema = z.object({
  id: z.uuid(),
  propertyId: refersTo('property', z.uuid()),
  sourceDocumentId: z.uuid().nullable(),
  status: z.enum(DOCUMENT_REDACTION_STATUSES),
  failureReason: z.enum(DOCUMENT_REDACTION_FAILURE_REASONS).nullable(),
  copyDocumentId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
});
export type DocumentRedactionResponse = z.infer<typeof DocumentRedactionSchema>;

export const DocumentRedactionPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  redactionId: z.uuid(),
});
